<template>
  <v-container>
    <!-- Cabeçalho da página -->
    <v-row class="align-center mb-5">
      <v-col cols="auto">
        <h3 class="page-title">
          <v-icon color="primary" class="mr-2">mdi-clipboard-text-outline</v-icon>
          Anamneses
        </h3>
      </v-col>

      <v-spacer />

      <v-col cols="auto">
        <!-- SALVIA-833: solicitar por WhatsApp, sem passar pelo assistente e sem
             consulta marcada. Fica ao lado do presencial de proposito: sao as
             duas formas de obter a mesma anamnese, e a escolha e do medico —
             a paciente responde depois, ou responde ali com ele. -->
        <v-btn
          color="primary"
          small
          outlined
          class="mr-3"
          @click="solicitarOpen = true"
        >
          <v-icon left small>mdi-whatsapp</v-icon>
          Solicitar por WhatsApp
        </v-btn>

        <!-- SALVIA-853: preencher a anamnese na consulta, com a paciente presente.
             As respostas caem nas mesmas tabelas das que vem pelo WhatsApp. -->
        <v-btn
          color="primary"
          small
          depressed
          class="mr-3"
          @click="abrirPresencial"
        >
          <v-icon left small>mdi-clipboard-account-outline</v-icon>
          Preencher na consulta
        </v-btn>

        <!-- Botão para reabrir o tour desta aba -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              aria-label="Ver o tour desta aba"
              class="mr-3"
              @click="startTour"
            >
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>Ver o tour desta aba</span>
        </v-tooltip>

        <!-- Recarregar -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="fetchAnamneses"
              :loading="loading"
              class="mr-3"
              data-tour="an2-refresh"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Atualizar lista</span>
        </v-tooltip>

        <!-- Ordenação por data -->
        <v-menu v-model="sortMenuOpen" :close-on-content-click="false" offset-y>
          <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                <v-btn
                  icon
                  color="primary"
                  v-bind="{ ...menuAttrs, ...tipAttrs }"
                  v-on="{ ...menuOn, ...tipOn }"
                >
                  <v-icon>mdi-sort</v-icon>
                </v-btn>
              </template>
              <span>Ordenar por data</span>
            </v-tooltip>
          </template>

          <v-list dense>
            <v-list-item @click="sortByDate('desc')">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-arrow-down</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Mais recentes primeiro</v-list-item-title>
            </v-list-item>
            <v-list-item @click="sortByDate('asc')">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-arrow-up</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Mais antigas primeiro</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Tabela -->
    <v-data-table
      :headers="headers"
      :items="anamneses"
      item-key="id"
      class="elevation-1"
      data-tour="an2-list"
      :loading="loading"
      loading-text="Carregando anamneses..."
      no-data-text="Nenhuma anamnese registrada para este paciente."
      :footer-props="{
        itemsPerPageText: 'Itens por página:',
        pageText: '{0}-{1} de {2}',
      }"
    >
      <template v-slot:item="{ item, index }">
        <tr>
          <td>{{ formatDate(item.date || item.created_at) }}</td>
          <td>
            <v-chip
              small
              :color="getTypeColor(item.type)"
              text-color="white"
              class="type-chip"
            >
              <v-icon size="14" left>{{ getTypeIcon(item.type) }}</v-icon>
              {{ getTypeLabel(item.type) }}
            </v-chip>
          </td>
          <td>{{ item.doctor_name || "—" }}</td>
          <td>
            <span class="session-cell" :title="item.session_id || ''">
              {{ truncate(item.session_id, 18) || "—" }}
            </span>
          </td>
          <td class="text-center">
            <v-chip
              small
              outlined
              color="primary"
              class="count-chip"
              :class="{ 'count-chip--zero': !item.medical_histories_count }"
            >
              {{ item.medical_histories_count || 0 }}
            </v-chip>
          </td>
          <td>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="openDetails(item)"
                  :data-tour="index === 0 ? 'an2-view' : null"
                >
                  <v-icon>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
              <span>Ver respostas</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Dialog de detalhes -->
    <AnamnesisDetailsDialog
      v-model="detailsOpen"
      :anamnese-id="selectedAnamneseId"
    />

    <BedsideAnamneseDialog
      v-model="presencialOpen"
      :patient-id="$route.params.id"
      @erro="(m) => error(m)"
      @concluida="fetchAnamneses"
    />

    <SolicitarAnamneseDialog
      v-model="solicitarOpen"
      :patient-id="$route.params.id"
      @erro="(m) => error(m)"
      @solicitada="anamneseSolicitada"
    />
  </v-container>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import AnamnesisDetailsDialog from "@/features/patients/components/AnamnesisDetailsDialog.vue";
import BedsideAnamneseDialog from "@/features/anamneses/BedsideAnamneseDialog.vue";
import SolicitarAnamneseDialog from "@/features/anamneses/SolicitarAnamneseDialog.vue";
import tourMixin from "@/features/tour/tourMixin";
import { formatDate } from "@/utils/datetime";

const TYPE_META = {
  1:       { label: "Geral",   color: "blue darken-1", icon: "mdi-clipboard-text-outline" },
  2:       { label: "Corpo",   color: "teal darken-1", icon: "mdi-human" },
  3:       { label: "Comida",  color: "orange darken-2", icon: "mdi-food-apple" },
  geral:   { label: "Geral",   color: "blue darken-1", icon: "mdi-clipboard-text-outline" },
  corpo:   { label: "Corpo",   color: "teal darken-1", icon: "mdi-human" },
  comida:  { label: "Comida",  color: "orange darken-2", icon: "mdi-food-apple" },
};

export default {
  name: "AnamnesisContainer",

  mixins: [tourMixin],

  components: {
    AnamnesisDetailsDialog,
    BedsideAnamneseDialog,
    SolicitarAnamneseDialog,
  },

  setup() {
    const { success, error, warning, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, erroDeRequisicao };
  },

  data() {
    return {
      loading: false,
      anamneses: [],
      sortMenuOpen: false,

      presencialOpen: false,
      solicitarOpen: false,
      detailsOpen: false,
      selectedAnamneseId: null,

      headers: [
        { text: "Data",      value: "date" },
        { text: "Tipo",      value: "type" },
        { text: "Médico",    value: "doctor_name" },
        { text: "Sessão",    value: "session_id" },
        { text: "Respostas", value: "medical_histories_count", align: "center" },
        { text: "Ações",     value: "actions", sortable: false, align: "center" },
      ],
    };
  },

  created() {
    this.fetchAnamneses();
  },

  computed: {
    tourId() {
      return "sec-anamneses-pg";
    },
  },

  methods: {
    /** SALVIA-853: abre a anamnese presencial para o paciente do prontuario. */
    abrirPresencial() {
      this.presencialOpen = true;
    },

    /**
     * SALVIA-833: a anamnese foi solicitada pelo WhatsApp.
     *
     * A lista NAO recebe linha nova agora: a anamnese so aparece aqui quando a
     * paciente comeca a responder. Dizer "solicitada" e nao "enviada" e
     * deliberado — o que saiu foi o pedido; a resposta depende dela.
     */
    anamneseSolicitada() {
      this.success("Anamnese solicitada. A paciente vai receber pelo WhatsApp.");
      this.fetchAnamneses();
    },

    formatDate: (v) => formatDate(v, { fallback: "—" }),
    tourReady() {
      return !this.loading;
    },

    tourSteps() {
      return [
        {
          title: "Anamneses do paciente",
          description:
            "Aqui você acompanha todas as anamneses já respondidas por este paciente. Deixe eu te mostrar o essencial.",
        },
        {
          anchor: "an2-list",
          title: "Lista de anamneses",
          description:
            "Cada linha é uma anamnese registrada, com data, tipo, profissional e quantas respostas ela contém.",
        },
        {
          anchor: "an2-view",
          title: "Ver respostas",
          description:
            "Clique aqui para abrir o formulário e ler todas as respostas dadas naquela anamnese.",
        },
        {
          anchor: "an2-refresh",
          title: "Atualizar a lista",
          description:
            "Acabou de responder uma anamnese em outra tela? Use este botão para recarregar e trazer os registros mais recentes.",
        },
      ];
    },

    async fetchAnamneses() {
      const patientId = this.$route.params.id;
      if (!patientId) {
        this.error("ID do paciente não encontrado na URL.");
        return;
      }

      this.loading = true;
      try {
        const { data } = await http.get(
          `/api/anamneses/by-patient/${patientId}`,
        );
        this.anamneses = Array.isArray(data?.data) ? data.data : [];

        if (this.anamneses.length === 0) {
          this.warning("Sem registro de anamnese para este paciente!");
        } else {
          this.success(
            `${this.anamneses.length} ${
              this.anamneses.length === 1
                ? "anamnese carregada"
                : "anamneses carregadas"
            } com sucesso`,
          );
        }
      } catch (err) {
        console.error("Erro ao carregar anamneses:", err);
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Erro ao carregar lista de anamneses.";
        this.erroDeRequisicao(err, msg);
        this.anamneses = [];
      } finally {
        this.loading = false;
      }
    },

    sortByDate(direction) {
      this.anamneses.sort((a, b) => {
        const da = new Date(a.date || a.created_at || 0).getTime();
        const db = new Date(b.date || b.created_at || 0).getTime();
        return direction === "asc" ? da - db : db - da;
      });
      this.sortMenuOpen = false;
    },

    openDetails(item) {
      this.selectedAnamneseId = item.id;
      this.detailsOpen = true;
    },

    truncate(str, n) {
      if (!str) return "";
      return str.length > n ? `${str.slice(0, n)}…` : str;
    },

    typeMeta(type) {
      if (type === null || type === undefined) return null;
      const key =
        typeof type === "string" ? type.toLowerCase().trim() : Number(type);
      return TYPE_META[key] || null;
    },

    getTypeLabel(type) {
      const m = this.typeMeta(type);
      if (m) return m.label;
      return type ? String(type) : "—";
    },

    getTypeColor(type) {
      return this.typeMeta(type)?.color || "blue-grey darken-1";
    },

    getTypeIcon(type) {
      return this.typeMeta(type)?.icon || "mdi-help-circle-outline";
    },
  },
};
</script>

<style scoped>
/* ═══ Page title ═══ */
.page-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.3px;
  margin: 0;
}

/* ═══ Tabela (mesma linguagem do Exams.vue) ═══ */
.v-data-table >>> thead th {
  background-color: var(--ds-blue-50) !important;
  border-bottom: 2px solid var(--ds-blue-100) !important;
  padding: 12px !important;
  font-size: 10px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.4px !important;
  color: var(--ds-primary) !important;
  font-weight: 700 !important;
  height: auto !important;
}

.v-data-table >>> tbody tr {
  transition: background-color 0.15s ease;
}

.v-data-table >>> tbody tr:hover {
  background-color: var(--ds-blue-50) !important;
}

.v-data-table >>> tbody td {
  border-bottom: 1px solid var(--ds-blue-divider) !important;
  font-size: 12px !important;
  color: var(--ds-text-600) !important;
}

/* ═══ Chip de tipo ═══ */
.type-chip {
  font-weight: 700 !important;
  letter-spacing: 0.2px;
  font-size: 11px !important;
}

/* ═══ Chip de contagem ═══ */
.count-chip {
  font-weight: 700 !important;
  font-size: 12px !important;
  min-width: 44px;
  justify-content: center;
}

.count-chip--zero {
  color: #A9D6C5 !important;
  border-color: var(--ds-blue-200) !important;
}

/* ═══ Coluna de sessão ═══ */
.session-cell {
  font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  font-size: 11.5px;
  color: var(--ds-text-500);
}
</style>
